import { useForm, router } from "@inertiajs/react";
import { useFlashToast } from "@/hooks/useFlashToast";
import { formatUnknownError } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Building2, Check, ChevronLeft, ChevronRight, Fingerprint, Pencil, Trash2, X } from "lucide-react";
import { startRegistration } from "@simplewebauthn/browser";
import { useState } from "react";

type User = {
  id: number;
  username: string;
  email: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
};

type Organization = {
  id: number;
  name: string;
};

type PasskeyEntry = {
  id: number;
  label: string | null;
  createdAt: string;
};

type Props = {
  user: User;
  organization: Organization | null;
  passkeys: PasskeyEntry[];
  errors?: Record<string, string[]>;
  flash?: { notice?: string; alert?: string };
};

const PASSKEYS_PER_PAGE = 10;

function PasskeyRow({
  pk,
  onDelete,
}: {
  pk: PasskeyEntry;
  onDelete: (id: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(pk.label || "");

  function handleSave() {
    router.patch(`/passkeys/${pk.id}`, { passkey: { label: editLabel || "Passkey" } }, {
      preserveScroll: true,
      onSuccess: () => setEditing(false),
    });
  }

  function handleCancel() {
    setEditLabel(pk.label || "");
    setEditing(false);
  }

  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <Fingerprint className="size-4 shrink-0 text-muted-foreground" />
        {editing ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Input
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              className="h-7 text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") handleCancel();
              }}
            />
            <Button variant="ghost" size="icon-xs" onClick={handleSave} aria-label="Save label">
              <Check className="size-3 text-green-600" />
            </Button>
            <Button variant="ghost" size="icon-xs" onClick={handleCancel} aria-label="Cancel editing">
              <X className="size-3" />
            </Button>
          </div>
        ) : (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium truncate">{pk.label || "Passkey"}</p>
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={() => setEditing(true)}
                aria-label={`Edit label for ${pk.label || "Passkey"}`}
              >
                <Pencil className="size-3 text-muted-foreground" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Added {new Date(pk.createdAt).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
      {!editing && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => onDelete(pk.id)}
          aria-label={`Remove passkey ${pk.label || "Passkey"}`}
        >
          <Trash2 className="size-4 text-destructive" />
        </Button>
      )}
    </div>
  );
}

export default function ProfileEdit({ user, organization, passkeys, errors = {}, flash }: Props) {
  useFlashToast(flash);

  const form = useForm({
    user: {
      username: user.username ?? "",
      first_name: user.firstName ?? "",
      middle_name: user.middleName ?? "",
      last_name: user.lastName ?? "",
    },
  });

  const [passkeyLabel, setPasskeyLabel] = useState("");
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [passkeySuccess, setPasskeySuccess] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const u = form.data.user;

  const paginated = passkeys.length > PASSKEYS_PER_PAGE;
  const totalPages = Math.ceil(passkeys.length / PASSKEYS_PER_PAGE);
  const visiblePasskeys = paginated
    ? passkeys.slice(page * PASSKEYS_PER_PAGE, (page + 1) * PASSKEYS_PER_PAGE)
    : passkeys;

  async function handleRegisterPasskey() {
    setPasskeyError(null);
    setPasskeySuccess(null);
    setPasskeyLoading(true);

    try {
      const optionsRes = await fetch("/passkey_registration/new");
      const options = await optionsRes.json();

      const credential = await startRegistration({ optionsJSON: options });

      const csrfToken =
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
          ?.content ?? "";

      const verifyRes = await fetch("/passkey_registration", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          credential,
          label: passkeyLabel || "Passkey",
        }),
      });

      const result = await verifyRes.json();

      if (verifyRes.ok) {
        setPasskeyLabel("");
        setPasskeySuccess("Passkey registered successfully.");
        router.reload({ only: ["passkeys"] });
      } else {
        setPasskeyError(result.error || "Failed to register passkey.");
      }
    } catch (err) {
      setPasskeyError(formatUnknownError(err));
    } finally {
      setPasskeyLoading(false);
    }
  }

  function handleDeletePasskey(id: number) {
    if (!confirm("Are you sure you want to remove this passkey?")) return;
    router.delete(`/passkeys/${id}`, { preserveScroll: true });
  }

  return (
    <div className="py-8 px-4">
      <div className="mx-auto max-w-2xl space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>Update your account details.</CardDescription>
          </CardHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.put("/profile");
            }}
          >
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First name</Label>
                  <Input
                    id="first_name"
                    type="text"
                    value={u.first_name}
                    onChange={(e) =>
                      form.setData("user", { ...u, first_name: e.target.value })
                    }
                    autoComplete="given-name"
                    aria-invalid={!!errors.first_name}
                  />
                  {errors.first_name?.[0] && (
                    <p className="text-sm text-destructive">{errors.first_name[0]}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last name</Label>
                  <Input
                    id="last_name"
                    type="text"
                    value={u.last_name}
                    onChange={(e) =>
                      form.setData("user", { ...u, last_name: e.target.value })
                    }
                    autoComplete="family-name"
                    aria-invalid={!!errors.last_name}
                  />
                  {errors.last_name?.[0] && (
                    <p className="text-sm text-destructive">{errors.last_name[0]}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="middle_name">Middle name</Label>
                <Input
                  id="middle_name"
                  type="text"
                  value={u.middle_name}
                  onChange={(e) =>
                    form.setData("user", { ...u, middle_name: e.target.value })
                  }
                  autoComplete="additional-name"
                  aria-invalid={!!errors.middle_name}
                />
                {errors.middle_name?.[0] && (
                  <p className="text-sm text-destructive">{errors.middle_name[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={u.username}
                  onChange={(e) =>
                    form.setData("user", { ...u, username: e.target.value })
                  }
                  autoComplete="username"
                  aria-invalid={!!errors.username}
                />
                {errors.username?.[0] && (
                  <p className="text-sm text-destructive">{errors.username[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={user.email ?? ""}
                  disabled
                  autoComplete="email"
                  className="disabled:opacity-60"
                />
                <p className="text-xs text-muted-foreground">
                  Email cannot be changed.
                </p>
              </div>
            </CardContent>
            <CardFooter>
              <Button type="submit" disabled={form.processing}>
                {form.processing ? "Saving…" : "Save changes"}
              </Button>
            </CardFooter>
          </form>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Organization</CardTitle>
            <CardDescription>
              Your assigned organization. This cannot be changed from your profile.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {organization ? (
              <div className="flex items-center gap-3 rounded-md border px-4 py-3">
                <Building2 className="size-4 text-muted-foreground" />
                <p className="text-sm font-medium">{organization.name}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                You are not currently assigned to an organization.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Passkeys</CardTitle>
            <CardDescription>
              Manage passkeys for passwordless sign-in.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {passkeyError && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {passkeyError}
              </div>
            )}
            {passkeySuccess && (
              <div className="rounded-md bg-green-500/10 px-3 py-2 text-sm text-green-700">
                {passkeySuccess}
              </div>
            )}

            {passkeys.length > 0 ? (
              <>
                <div className="divide-y rounded-md border">
                  {visiblePasskeys.map((pk) => (
                    <PasskeyRow key={pk.id} pk={pk} onDelete={handleDeletePasskey} />
                  ))}
                </div>

                {paginated && (
                  <div className="flex items-center justify-between pt-1">
                    <p className="text-xs text-muted-foreground">
                      {passkeys.length} passkeys &middot; page {page + 1} of {totalPages}
                    </p>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon-sm"
                        disabled={page === 0}
                        onClick={() => setPage((p) => p - 1)}
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="size-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon-sm"
                        disabled={page >= totalPages - 1}
                        onClick={() => setPage((p) => p + 1)}
                        aria-label="Next page"
                      >
                        <ChevronRight className="size-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                No passkeys registered yet.
              </p>
            )}

            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-2">
                <Label htmlFor="passkey_label">Label</Label>
                <Input
                  id="passkey_label"
                  type="text"
                  placeholder='e.g. "MacBook Pro"'
                  value={passkeyLabel}
                  onChange={(e) => setPasskeyLabel(e.target.value)}
                />
              </div>
              <Button
                onClick={handleRegisterPasskey}
                disabled={passkeyLoading}
              >
                <Fingerprint className="size-4" />
                {passkeyLoading ? "Registering…" : "Add passkey"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
